import React, {useState, useMemo} from "react"
import {
   FormControl,
   InputLabel,
   Select,
   MenuItem,
   OutlinedInput,
   Box,
   Chip,
   TextField,
   SelectChangeEvent,
   ListSubheader,
} from "@mui/material"
import CheckIcon from "@mui/icons-material/Check"
import {CDBLUE} from "@/constants"

interface OptionType {
   value: string | number
   label: string
}
interface AssociatesSelectProps {
   label?: string
   value: Array<string | number>
   onChange: (event: SelectChangeEvent<(string | number)[]>, child?: React.ReactNode) => void
   options?: OptionType[]
   showAllSelected?: boolean
}

export default function AssociatesSelect({
   label = "Asociados/Responsables",
   value,
   onChange,
   options = [],
   showAllSelected = false,
}: AssociatesSelectProps) {
   const [searchTerm, setSearchTerm] = useState("")

   const filteredOptions = useMemo(() => {
      const term = searchTerm.trim().toLowerCase()
      if (!term) return options
      return options.filter((opt) => opt.label.toLowerCase().includes(term))
   }, [options, searchTerm])

   return (
      <FormControl size="small">
         <InputLabel>{label}</InputLabel>
         <Select
            multiple
            value={value}
            onChange={onChange}
            input={<OutlinedInput label={label} />}
            onClose={() => setSearchTerm("")} // limpia el buscador al cerrar
            renderValue={(selected) => {
               const selectedOptions = selected
                  .map((val) => options.find((o) => o.value === val))
                  .filter(Boolean) as OptionType[]
               const selectedLabelLength = selectedOptions.reduce(
                  (sum, option) => sum + option.label.length,
                  0,
               )
               const useCompactView =
                  !showAllSelected && (selectedOptions.length > 2 || selectedLabelLength > 32)
               const visibleLabelCount = useCompactView ? 1 : Math.min(2, selectedOptions.length)
               const displayOptions = showAllSelected
                  ? selectedOptions
                  : selectedOptions.slice(0, visibleLabelCount)
               const remainingCount = selectedOptions.length - displayOptions.length

               return (
                  <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center"}}>
                     {displayOptions.map((option) => (
                        <Chip
                           key={option.value}
                           label={option.label}
                           sx={{bgcolor: CDBLUE, color: "white"}}
                        />
                     ))}
                     {remainingCount > 0 && (
                        <Chip label={`+${remainingCount}`} sx={{bgcolor: CDBLUE, color: "white"}} />
                     )}
                  </Box>
               )
            }}
            MenuProps={{
               PaperProps: {style: {maxHeight: 300}},
               autoFocus: false,
               disableAutoFocusItem: true,
            }}
         >
            {/* Input de búsqueda fijo (no seleccionable) */}
            <ListSubheader sx={{bgcolor: "background.paper"}}>
               <TextField
                  size="small"
                  placeholder="Buscar asociados..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  fullWidth
                  autoFocus
               />
            </ListSubheader>

            {filteredOptions.length === 0 ? (
               <MenuItem disabled>
                  <em>Sin resultados</em>
               </MenuItem>
            ) : (
               filteredOptions.map((opt) => {
                  const isSelected = value.includes(opt.value)
                  return (
                     <MenuItem
                        key={opt.value}
                        value={opt.value}
                        sx={{
                           display: "flex",
                           justifyContent: "space-between",
                           backgroundColor: isSelected ? `${CDBLUE}15` : "transparent",
                           "&:hover": {
                              backgroundColor: isSelected ? `${CDBLUE}25` : "rgba(0, 0, 0, 0.04)",
                           },
                        }}
                     >
                        <span>{opt.label}</span>
                        {isSelected && (
                           <CheckIcon sx={{color: CDBLUE, fontSize: "1.1rem", marginLeft: 1}} />
                        )}
                     </MenuItem>
                  )
               })
            )}
         </Select>
      </FormControl>
   )
}
